<template>
  <div class="contract-tab">
    <div class="tab-header">
      <h4>임가공 계약</h4>
      <el-button v-if="canEdit" type="primary" size="small" @click="openForm()">
        계약 추가
      </el-button>
    </div>

    <el-table :data="contracts" border size="small" stripe v-loading="loading">
      <el-table-column prop="contract_number" label="계약번호" width="120" />
      <el-table-column prop="manufacturer_name" label="생산공장" min-width="120" />
      <el-table-column prop="contract_date" label="계약일" width="110" />
      <el-table-column label="임가공임" width="110" align="right">
        <template #default="{ row }">
          {{ formatPrice(row.cmt_price) }} {{ row.currency }}
        </template>
      </el-table-column>
      <el-table-column label="환율" width="90" align="right">
        <template #default="{ row }">
          {{ row.exchange_rate }}
        </template>
      </el-table-column>
      <el-table-column prop="quantity" label="계약수량" width="100" align="right">
        <template #default="{ row }">
          {{ row.quantity?.toLocaleString() }}
        </template>
      </el-table-column>
      <el-table-column label="총 CMT" width="130" align="right">
        <template #default="{ row }">
          {{ formatPrice(row.total_cmt_cost) }} {{ row.currency }}
        </template>
      </el-table-column>
      <el-table-column label="원화환산" width="130" align="right">
        <template #default="{ row }">
          {{ formatKrw(row.total_cmt_cost_krw) }}
        </template>
      </el-table-column>
      <el-table-column prop="delivery_date" label="납기일" width="110" />
      <el-table-column label="상태" width="90" align="center">
        <template #default="{ row }">
          <el-tag :type="statusType(row.status)" size="small">
            {{ row.status_display }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="계약서" width="80" align="center">
        <template #default="{ row }">
          <el-link
            v-if="row.contract_file"
            :href="row.contract_file"
            target="_blank"
            type="primary"
            :underline="false"
          >
            <el-icon><Document /></el-icon>
          </el-link>
          <span v-else style="color:#c0c4cc">-</span>
        </template>
      </el-table-column>
      <el-table-column label="" width="80" align="center" v-if="canEdit">
        <template #default="{ row }">
          <el-button type="primary" link size="small" @click="openForm(row)">수정</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-empty v-if="contracts.length === 0 && !loading" description="등록된 임가공 계약이 없습니다." />

    <!-- 계약 폼 다이얼로그 -->
    <el-dialog
      v-model="formVisible"
      :title="editingId ? '임가공 계약 수정' : '임가공 계약 추가'"
      width="700px"
    >
      <el-form :model="form" label-width="100px" :rules="formRules" ref="formRef">
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="계약번호">
              <el-input v-model="form.contract_number" placeholder="계약번호" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="생산공장" prop="manufacturer">
              <el-select
                v-model="form.manufacturer"
                filterable
                placeholder="공장 선택"
                style="width: 100%"
              >
                <el-option
                  v-for="s in suppliers"
                  :key="s.id"
                  :label="s.name"
                  :value="s.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="계약일" prop="contract_date">
              <el-date-picker
                v-model="form.contract_date"
                type="date"
                value-format="YYYY-MM-DD"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="납기일" prop="delivery_date">
              <el-date-picker
                v-model="form.delivery_date"
                type="date"
                value-format="YYYY-MM-DD"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="8">
            <el-form-item label="임가공임" prop="cmt_price">
              <el-input-number v-model="form.cmt_price" :precision="2" :min="0" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="통화">
              <el-select v-model="form.currency" style="width: 100%">
                <el-option label="USD" value="USD" />
                <el-option label="KRW" value="KRW" />
                <el-option label="CNY" value="CNY" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="환율">
              <el-input-number v-model="form.exchange_rate" :precision="2" :min="0" style="width: 100%" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="계약수량" prop="quantity">
          <el-input-number v-model="form.quantity" :min="1" style="width: 100%" />
        </el-form-item>

        <!-- 거래 조건 -->
        <el-divider content-position="left">거래 조건</el-divider>
        <el-row :gutter="16">
          <el-col :span="8">
            <el-form-item label="가격조건">
              <el-select v-model="form.price_term" style="width: 100%">
                <el-option label="FOB" value="FOB" />
                <el-option label="CIF" value="CIF" />
                <el-option label="CFR" value="CFR" />
                <el-option label="EXW" value="EXW" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="결제조건">
              <el-select v-model="form.payment_term" clearable style="width: 100%">
                <el-option label="AT SIGHT L/C" value="AT SIGHT L/C" />
                <el-option label="USANCE L/C" value="USANCE L/C" />
                <el-option label="T/T" value="T/T" />
                <el-option label="D/P" value="D/P" />
                <el-option label="D/A" value="D/A" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="허용률(±%)">
              <el-input-number v-model="form.quantity_tolerance" :precision="2" :min="0" :max="10" style="width: 100%" />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 담당자/상태 -->
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="담당자">
              <el-input v-model="form.contact_name" placeholder="담당자명" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="연락처">
              <el-input v-model="form.contact_phone" placeholder="연락처" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="상태">
          <el-select v-model="form.status" style="width: 100%">
            <el-option label="초안" value="DRAFT" />
            <el-option label="확정" value="CONFIRMED" />
            <el-option label="진행중" value="IN_PROGRESS" />
            <el-option label="완료" value="COMPLETED" />
            <el-option label="취소" value="CANCELLED" />
          </el-select>
        </el-form-item>

        <!-- 수익자 은행 정보 -->
        <el-collapse>
          <el-collapse-item title="수익자 은행 정보 (Beneficiary Bank)">
            <el-row :gutter="16">
              <el-col :span="12">
                <el-form-item label="은행명">
                  <el-input v-model="form.bank_name" placeholder="은행명" />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="SWIFT">
                  <el-input v-model="form.bank_swift" placeholder="SWIFT 코드" />
                </el-form-item>
              </el-col>
            </el-row>
            <el-form-item label="계좌번호">
              <el-input v-model="form.bank_account" placeholder="계좌번호 (A/C NO.)" />
            </el-form-item>
            <el-form-item label="은행주소">
              <el-input v-model="form.bank_address" placeholder="은행 주소" />
            </el-form-item>
          </el-collapse-item>
        </el-collapse>

        <!-- 계약서 원본 파일 -->
        <el-divider content-position="left">계약서 원본</el-divider>
        <el-form-item label="파일">
          <div class="contract-file-area">
            <el-link
              v-if="form.contract_file && typeof form.contract_file === 'string'"
              :href="form.contract_file"
              target="_blank"
              type="primary"
            >
              <el-icon><Document /></el-icon>
              현재 파일 보기
            </el-link>
            <el-upload
              action="#"
              :auto-upload="false"
              :on-change="handleFileChange"
              :limit="1"
              :file-list="fileList"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            >
              <el-button size="small" type="primary">
                {{ form.contract_file ? '파일 변경' : '파일 첨부' }}
              </el-button>
              <template #tip>
                <div class="el-upload__tip">PDF, 이미지, Word (10MB 이내)</div>
              </template>
            </el-upload>
          </div>
        </el-form-item>

        <el-form-item label="비고" style="margin-top: 12px">
          <el-input v-model="form.memo" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="formVisible = false">취소</el-button>
        <el-button
          v-if="editingId && canEdit"
          type="danger"
          @click="handleDelete"
        >
          삭제
        </el-button>
        <el-button type="primary" @click="handleSave" :loading="saving">
          저장
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { Document } from '@element-plus/icons-vue'
import axiosInstance from '@/api/axios'
import { ElMessage, ElMessageBox } from 'element-plus'

const props = defineProps({
  mainRequestId: { type: [Number, String], required: true },
  canEdit: { type: Boolean, default: false },
  orderQuantity: { type: Number, default: 0 },
})

const loading = ref(false)
const saving = ref(false)
const contracts = ref([])
const suppliers = ref([])
const formVisible = ref(false)
const formRef = ref(null)
const editingId = ref(null)

const defaultForm = () => ({
  contract_number: '',
  manufacturer: null,
  contract_date: null,
  delivery_date: null,
  cmt_price: 0,
  currency: 'USD',
  exchange_rate: 1,
  quantity: props.orderQuantity || 0,
  status: 'DRAFT',
  price_term: 'FOB',
  payment_term: '',
  quantity_tolerance: 1,
  contact_name: '',
  contact_phone: '',
  bank_name: '',
  bank_account: '',
  bank_swift: '',
  bank_address: '',
  memo: '',
})

const form = ref(defaultForm())
const fileList = ref([])
const pendingFile = ref(null)

const handleFileChange = (file) => {
  if (file.size > 10 * 1024 * 1024) {
    ElMessage.error('파일 크기는 10MB 이하여야 합니다.')
    fileList.value = []
    return
  }
  pendingFile.value = file.raw
}

const formRules = {
  manufacturer: [{ required: true, message: '공장을 선택하세요', trigger: 'change' }],
  contract_date: [{ required: true, message: '계약일을 입력하세요', trigger: 'change' }],
  delivery_date: [{ required: true, message: '납기일을 입력하세요', trigger: 'change' }],
  cmt_price: [{ required: true, message: '임가공임을 입력하세요', trigger: 'blur' }],
  quantity: [{ required: true, message: '수량을 입력하세요', trigger: 'blur' }],
}

const fetchContracts = async () => {
  if (!props.mainRequestId) return
  loading.value = true
  try {
    const res = await axiosInstance.get(
      `production/main-requests/${props.mainRequestId}/contracts/`
    )
    contracts.value = res.data
  } catch (e) {
    console.error('계약 조회 실패:', e)
  } finally {
    loading.value = false
  }
}

const fetchSuppliers = async () => {
  try {
    const res = await axiosInstance.get('/production/manufacturers/', {
      params: { active: true }
    })
    suppliers.value = res.data.results || res.data || []
  } catch (e) {
    console.error('공급업체 조회 실패:', e)
  }
}

const openForm = (row = null) => {
  if (row) {
    editingId.value = row.id
    form.value = { ...row }
  } else {
    editingId.value = null
    form.value = defaultForm()
  }
  fileList.value = []
  pendingFile.value = null
  formVisible.value = true
}

const buildFormData = () => {
  const fd = new FormData()
  const fields = [
    'contract_number', 'manufacturer', 'contract_date', 'delivery_date',
    'cmt_price', 'currency', 'exchange_rate', 'quantity', 'status',
    'price_term', 'payment_term', 'quantity_tolerance',
    'contact_name', 'contact_phone',
    'bank_name', 'bank_account', 'bank_swift', 'bank_address', 'memo',
  ]
  for (const key of fields) {
    const val = form.value[key]
    if (val !== null && val !== undefined && val !== '') {
      fd.append(key, val)
    }
  }
  fd.append('main_request', props.mainRequestId)
  if (pendingFile.value) {
    fd.append('contract_file', pendingFile.value)
  }
  return fd
}

const handleSave = async () => {
  if (formRef.value) {
    try {
      await formRef.value.validate()
    } catch {
      return
    }
  }
  saving.value = true
  try {
    const data = buildFormData()
    const config = { headers: { 'Content-Type': 'multipart/form-data' } }
    if (editingId.value) {
      await axiosInstance.put(
        `production/contracts/${editingId.value}/`,
        data, config
      )
      ElMessage.success('계약이 수정되었습니다.')
    } else {
      await axiosInstance.post(
        `production/main-requests/${props.mainRequestId}/contracts/`,
        data, config
      )
      ElMessage.success('계약이 추가되었습니다.')
    }
    formVisible.value = false
    await fetchContracts()
  } catch (e) {
    ElMessage.error('저장 실패: ' + (e.response?.data?.detail || '오류 발생'))
  } finally {
    saving.value = false
  }
}

const handleDelete = async () => {
  try {
    await ElMessageBox.confirm('이 계약을 삭제하시겠습니까?', '삭제 확인', {
      confirmButtonText: '삭제',
      cancelButtonText: '취소',
      type: 'warning',
    })
    await axiosInstance.delete(`production/contracts/${editingId.value}/`)
    ElMessage.success('삭제되었습니다.')
    formVisible.value = false
    await fetchContracts()
  } catch {
    // 취소
  }
}

const formatPrice = (val) => {
  if (!val) return '0'
  return parseFloat(val).toLocaleString(undefined, { minimumFractionDigits: 2 })
}

const formatKrw = (val) => {
  if (!val) return '-'
  return '₩' + Math.round(parseFloat(val)).toLocaleString()
}

const statusType = (status) => {
  const map = {
    DRAFT: 'info',
    CONFIRMED: 'primary',
    IN_PROGRESS: 'warning',
    COMPLETED: 'success',
    CANCELLED: 'danger',
  }
  return map[status] || 'info'
}

watch(() => props.mainRequestId, fetchContracts, { immediate: true })

onMounted(() => {
  fetchContracts()
  fetchSuppliers()
})
</script>

<style scoped>
.contract-tab {
  padding: 8px 0;
}
.tab-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.tab-header h4 {
  margin: 0;
  font-size: 15px;
}
.contract-file-area {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
</style>
